import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

function QcList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [qcRequests, setQcRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedQc, setSelectedQc] = useState(null);
  const [reviewData, setReviewData] = useState({
    status: 'approved',
    notes: '',
    reportFile: null
  });

  useEffect(() => {
    fetchQcRequests();
  }, []);

  const fetchQcRequests = async () => {
    try {
      const response = await api.getQcRequests();
      setQcRequests(response.data);
    } catch (error) {
      console.error('QC talepleri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Bekliyor';
      case 'approved': return 'Onaylandı';
      case 'rejected': return 'Reddedildi';
      default: return status;
    }
  };

  const openReviewModal = (qc) => {
    setSelectedQc(qc);
    setReviewData({
      status: 'approved',
      notes: '',
      reportFile: null
    });
    setShowReviewModal(true);
  };

  const handleReview = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('status', reviewData.status);
      formData.append('notes', reviewData.notes);
      if (reviewData.reportFile) {
        formData.append('reportFile', reviewData.reportFile);
      }

      await api.reviewQc(selectedQc.id, formData);
      fetchQcRequests();
      setShowReviewModal(false);
    } catch (error) {
      alert(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const filteredRequests = qcRequests.filter(qc => {
    if (filter === 'all') return true;
    return qc.status === filter;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    return timeStr.substring(0, 5);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Kalite Kontrol Talepleri</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Tümü ({qcRequests.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Bekleyen ({qcRequests.filter(q => q.status === 'pending').length})
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'approved' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Onaylı ({qcRequests.filter(q => q.status === 'approved').length})
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'rejected' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Reddedilen ({qcRequests.filter(q => q.status === 'rejected').length})
          </button>
        </div>
      </div>

      {/* QC Requests Grid */}
      <div className="grid gap-4">
        {filteredRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            Kayıt bulunamadı
          </div>
        ) : (
          filteredRequests.map((qc) => (
            <div key={qc.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {qc.Process?.name || 'Proses'}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(qc.status)}`}>
                      {getStatusLabel(qc.status)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">İş Emri:</span>
                      <p className="font-medium">
                        <button
                          onClick={() => navigate(`/work-orders/${qc.Process?.workOrderId}`)}
                          className="text-blue-600 hover:underline"
                        >
                          {qc.Process?.WorkOrder?.orderNumber || '-'}
                        </button>
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">İmalatçı:</span>
                      <p className="font-medium">{qc.Process?.WorkOrder?.Assignments?.[0]?.Company?.name || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Talep Tarihi:</span>
                      <p className="font-medium">{formatDate(qc.requestedDate)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Talep Saati:</span>
                      <p className="font-medium">{formatTime(qc.requestedTime)}</p>
                    </div>
                  </div>

                  {qc.notes && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-500 text-sm">Notlar:</span>
                      <p className="text-sm">{qc.notes}</p>
                    </div>
                  )}

                  {qc.reviewNotes && (
                    <div className={`mt-3 p-3 rounded-lg ${
                      qc.status === 'approved' ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                      <span className="text-gray-500 text-sm">İnceleme Notu:</span>
                      <p className="text-sm">{qc.reviewNotes}</p>
                      {qc.reviewedBy && (
                        <p className="text-xs text-gray-400 mt-1">
                          İnceleyen: {qc.ReviewedByUser?.name} - {formatDate(qc.reviewedAt)}
                        </p>
                      )}
                    </div>
                  )}

                  {qc.reportFile && (
                    <div className="mt-3">
                      <a
                        href={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${qc.reportFile}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        QC Raporu
                      </a>
                    </div>
                  )}
                </div>

                {qc.status === 'pending' && (user.userType === 'admin' || user.userType === 'tis') && (
                  <button
                    onClick={() => openReviewModal(qc)}
                    className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    İncele
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedQc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">QC İnceleme</h2>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm"><strong>Proses:</strong> {selectedQc.Process?.name}</p>
              <p className="text-sm"><strong>Talep Tarihi:</strong> {formatDate(selectedQc.requestedDate)} {formatTime(selectedQc.requestedTime)}</p>
            </div>
            <form onSubmit={handleReview} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Karar
                </label>
                <select
                  value={reviewData.status}
                  onChange={(e) => setReviewData({ ...reviewData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="approved">Onayla (+20 Puan)</option>
                  <option value="rejected">Reddet (-30 Puan)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notlar
                </label>
                <textarea
                  value={reviewData.notes}
                  onChange={(e) => setReviewData({ ...reviewData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="İnceleme notları..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  QC Raporu (PDF)
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setReviewData({ ...reviewData, reportFile: e.target.files[0] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowReviewModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 text-white rounded-lg ${
                    reviewData.status === 'approved'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {reviewData.status === 'approved' ? 'Onayla' : 'Reddet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default QcList;
